import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FiMapPin,
  FiPhone,
  FiClock,
  FiCheckCircle,
} from "react-icons/fi";
import { AiFillStar } from "react-icons/ai";
import { LuHotel, LuUtensils, LuTrees, LuScissors } from "react-icons/lu";
import { fetchPetPlaces } from "../../redux/slices/petPlacesSlice";
import { MdOutlineArrowBackIos } from "react-icons/md";
import { useNavigate } from "react-router-dom";

// Tabs config
const tabs = [
  { key: "hotels", label: "Hotels", Icon: LuHotel },
  { key: "restaurants", label: "Restaurants", Icon: LuUtensils },
  { key: "parks", label: "Parks", Icon: LuTrees },
  { key: "services", label: "Services", Icon: LuScissors },
];

// Pill component
function Pill({ children }) {
  return (
    <span className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700">
      {children}
    </span>
  );
}

// Row component
function Row({ icon, text }) {
  return (
    <div className="flex items-center gap-3 text-sm text-gray-600">
      <span className="text-gray-400">{icon}</span>
      <span className="truncate">{text}</span>
    </div>
  );
}

// Card component
function Card({ item, emblem }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-gray-100 text-gray-500">
            {emblem}
          </div>
          <h3 className="text-base font-semibold text-gray-900">{item.name}</h3>
          <span className="ml-2 inline-flex items-center gap-1 rounded-md bg-[#D8FFE9] px-2 py-0.5 text-[11px] font-medium text-green-500">
            <FiCheckCircle className="h-3.5 w-3.5" />
            Verified
          </span>
        </div>
        <div className="flex items-center gap-1 text-sm font-semibold text-gray-800">
          <AiFillStar className="h-4 w-4 text-amber-400" />
          {item.rating}
        </div>
      </div>

      <div className="mt-2 text-sm text-gray-500">{item.type}</div>
      <p className="mt-1 text-sm leading-6 text-gray-600">{item.desc}</p>

      <div className="mt-4 space-y-2">
        <Row icon={<FiMapPin className="h-4 w-4" />} text={item.address} />
        <Row icon={<FiPhone className="h-4 w-4" />} text={item.phone} />
        <Row icon={<FiClock className="h-4 w-4" />} text={item.hours} />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {item.tags?.map((t, i) => (
          <Pill key={i}>{t}</Pill>
        ))}
      </div>

      <div className="mt-4 text-sm font-semibold text-gray-900">{item.price}</div>
    </div>
  );
}

// PetPlaces component
export default function PetPlaces() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const petPlacesState = useSelector((state) => state.petPlaces);
  const { items, status } = petPlacesState;

  const [active, setActive] = useState("hotels");

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchPetPlaces());
    }
  }, [dispatch, status]);

  // Get items dynamically from Redux
  const itemsToShow = useMemo(() => items?.[active] || [], [active, items]);

  const TabButton = ({ id, label }) => (
    <button
      onClick={() => setActive(id)}
      className={[
        "rounded-lg px-16 py-3 text-sm font-medium transition-colors",
        active === id
          ? "border border-gray-200 bg-white text-gray-900 shadow-sm"
          : "text-gray-500 hover:text-gray-800",
      ].join(" ")}
    >
      {label}
    </button>
  );

  const Emblem = () => {
    const size = 16;
    switch (active) {
      case "hotels":
        return <LuHotel size={size} />;
      case "restaurants":
        return <LuUtensils size={size} />;
      case "parks":
        return <LuTrees size={size} />;
      case "services":
        return <LuScissors size={size} />;
      default:
        return null;
    }
  };

  if (status === "loading") {
    return (
      <div className="px-3 sm:px-6 md:px-8 lg:px-12 pt-6 md:pt-8 font-inter min-h-screen bg-white">
        <div className="text-center text-gray-500 py-10 text-sm md:text-base">
          Loading pet places...
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 sm:px-6 md:px-8 lg:px-12 pt-6 md:pt-8 font-inter min-h-screen bg-white">
      {/* ✅ Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        {/* Left section */}
        <div className="flex items-start md:items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            aria-label="Go Back"
            className="p-2 md:p-3 text-lg md:text-xl text-gray-800 hover:text-blue-600"
          >
            <MdOutlineArrowBackIos />
          </button>
          <div>
            <div className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900">
              Pet-Friendly Places
            </div>
            <div className="text-gray-600 text-sm sm:text-base">
              Discover hotels, restaurants, parks, and services that welcome pets
            </div>
          </div>
        </div>

        {/* Add Button */}
        <button
          onClick={() => navigate("/admin/pet-places/add")}
          className="flex items-center justify-center gap-2 bg-blue-500 text-white text-sm sm:text-base font-medium px-5 sm:px-8 py-2 sm:py-2.5 rounded-lg shadow hover:bg-blue-600 transition w-full sm:w-auto"
        >
          Add
          <span className="rounded bg-blue-500 px-1.5 py-0.5 text-xs">+</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="mt-6 flex items-center justify-center gap-18">
        {tabs.map((t) => (
          <TabButton key={t.key} id={t.key} label={t.label} />
        ))}
      </div>

      {/* Grid */}
      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        {itemsToShow.map((item, idx) => (
          <Card key={idx} item={item} emblem={<Emblem />} />
        ))}
      </div>
    </div>
  );
}
